import { HttpProvider } from "../http/http.provider";
import { ServiceEndpointConstants } from "../ServiceUtil";
import { Observable } from "rxjs";
import { share, map } from "rxjs/operators";
import { IHttpRequestConfig } from "../http/types/http.types";
import { createFormData } from "../util/utilities";
import
{
    IEmployee,
    IEmployeeAddress,
    IUpdateEmployeeResponse,
    IEmpPayroll,
    IEmpTaxDoc,
    INewEmployeeAddress,
    INewEmployeeDocument,
    INewEmploymentHistory,
    INewEmployeeAbsence,
    IEmployeeAbsence
} from "./employee-side.interface";

/**
 * HTTP delegate for the employee self-service API surface (own profile, own
 * paystubs, own tax docs, own absences, paperless toggle).
 */
export class EmployeeSideDelegate
{
    constructor(private http: HttpProvider) { }


    public getEmployeeByEmpId(employeeId: string): Observable<IEmployee>
    {
        const config: IHttpRequestConfig = { params: { EmployeeId: employeeId } };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET_EMPLOYEE_BY_ID, null, config).pipe(
            map((response: any) => response.data.employee as IEmployee),
            share());
    }

    public getEmployeePaystubsByEmpId(employeeId: string, year: number): Observable<IEmpPayroll[]>
    {
        const config: IHttpRequestConfig = { params: { EmployeeId: employeeId, Year: year } };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET_EMPLOYEE_PAYSTUBS_BY_ID, null, config).pipe(
            map((response: any) => response.data.employeePaystubs as IEmpPayroll[]),
            share());
    }

    public getEmployeeTaxDocsByEmpId(employeeId: string, year: number): Observable<IEmpTaxDoc[]>
    {
        const config: IHttpRequestConfig = { params: { EmployeeId: employeeId, Year: year } };
        return this.http.get(ServiceEndpointConstants.endpoints.EMPLOYEES.GET_EMPLOYEE_TAXDOCS_BY_ID, null, config).pipe(
            map((response: any) => response.data.employeeTaxDocs as IEmpTaxDoc[]),
            share());
    }

    public updateEmployeePaperless(employeeId: string, paperless: boolean): Observable<IEmployee>
    {
        const body = { EmployeeId: employeeId, Paperless: paperless };
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.UPDATE_EMPLOYEE_PAPERLESS, body).pipe(
            map((response: any) => response.data.employee as IEmployee),
            share());
    }

    public createEmployeeAddress(newEmployeeAddress: INewEmployeeAddress): Observable<IUpdateEmployeeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.SAVE_EMPLOYEE_ADDRESS, newEmployeeAddress).pipe(
            map((response: any) => ({
                employees: response.data.employees as IEmployee[],
                id: response.data.newEmployeeAddressId as string
            } as IUpdateEmployeeResponse)),
            share());
    }

    public createEmployeeDocuments(documents: INewEmployeeDocument): Observable<IEmployee[]>
    {
        const formdata = createFormData(documents);
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EMPLOYEE_DOCUMENT, formdata).pipe(
            map((response: any) => response.data.employees as IEmployee[]),
            share());
    }

    public createEmployeeHistory(history: INewEmploymentHistory): Observable<IEmployee[]>
    {
        const formdata = createFormData(history);
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EMPLOYEE_HISTORY, formdata).pipe(
            map((response: any) => response.data.employees as IEmployee[]),
            share());
    }

    public createEmployeeAbsence(employeeAbsence: INewEmployeeAbsence): Observable<boolean>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.EMPLOYEES.CREATE_EMPLOYEE_ABSENCE, employeeAbsence).pipe(
            map((response: any) => response.data.success as boolean),
            share());
    }
}

